import * as ArrayUtils from '../../utils/array-utils';
import { removeMatching } from '../../utils/array-utils';
import { appAlertComponentModule } from '../../components/app-alert/app-alert';
import { multiFilePathDialogComponentModule } from '../../components/multi-file-path-dialog/multi-file-path-dialog';
import { multiFileOverwriteDialogComponentModule } from '../../components/multi-file-overwrite-dialog/multi-file-overwrite-dialog';
import { multiFileChallengeServiceSmartIdeModule } from './multi-file-challenge-service-smart-ide';
import { codeFileModelModule } from '../../models/code-file';
import { virtualFileDownloadServiceModule } from '../virtual-file-download-service/virtual-file-download-service';
import { cleanPath, parentPath } from '../../utils/file-utils/file-utils';
import { escapeRegex } from '../../utils/string-utils';
import { dialogComponentModule } from '../../components/dialog/dialog';
import { multiFileChallengeServiceDragAnDropModule } from './multi-file-challenge-service-drag-and-drop';
import { MAX_TOTAL_FILES } from './multi-file-challenge-service-constants';

// prevent matching a leading directory _or_ any entire path
// the (/|$) means inline slash or end-of-line
export const existingFileRegex = (path, exact = false) =>
  new RegExp(`^${escapeRegex(cleanPath(path))}${exact ? '$' : '(/|$)'}`, 'i');

export const multiFileChallengeServiceFileManagementModule = angular
  .module('Q.services.multi-file-challenge-service-fm', [
    appAlertComponentModule,
    multiFilePathDialogComponentModule,
    multiFileOverwriteDialogComponentModule,
    multiFileChallengeServiceDragAnDropModule,
    multiFileChallengeServiceSmartIdeModule,
    codeFileModelModule,
    virtualFileDownloadServiceModule,
    dialogComponentModule,
  ])

  .factory(
    'multiFileChallengeServiceFM',
    function (
      $timeout,
      appAlert,
      multiFilePathDialog,
      multiFileOverwriteDialog,
      AdvancedCodeChallengeFile,
      AdvancedCodeChallengeReferenceFile,
      AdvancedCodeSolutionFile,
      virtualFileDownloadService,
      dialogService,
      multiFileChallengeServiceDND,
      multiFileChallengeServiceSmartIde,
    ) {
      const REFERENCE_PREFIX = '[Reference]/';
      const REFERENCE_PREFIX_REGEX = /^\[Reference](\/|$)/;

      function setupFileManagement(svc, opts) {
        let workspace;
        multiFileChallengeServiceSmartIde.setupSmartIde(svc, opts, (ws) => {
          workspace = ws;
        });

        const _files = (reference) =>
          reference ? svc.referenceFiles || [] : svc.files;
        const _alertOnTooManyFiles = (props, reference = props.reference) => {
          if (
            !props.directory &&
            _files(reference).reduce((s, f) => s + (f.directory ? 0 : 1), 0) >=
              MAX_TOTAL_FILES
          ) {
            appAlert.warning(
              `Too many files: projects are limited to ${MAX_TOTAL_FILES} files. Please delete some before continuing.`,
            );
            return true;
          }
        };

        const _checkForExistingPath = (path, reference, exact = false) => {
          const pathTest = existingFileRegex(path, exact);
          // check for full path _OR_ leading
          return _files(reference).find((f) => pathTest.test(f.path));
        };
        const _checkForExistingParentDirectory = (path, reference) => {
          const parent = parentPath(path);
          return _files(reference).find(
            (f) => !f.directory && parent.startsWith(f.path),
          );
        };
        const _checkForExisting = (path, reference) => {
          return (
            _checkForExistingPath(path, reference) ||
            _checkForExistingParentDirectory(path, reference)
          );
        };

        const _invalidCheck = (existingPath, reference, checkBoth) => {
          return (newPath, renameReference) => {
            if (
              !angular.isString(existingPath) ||
              newPath.toLowerCase() !== existingPath.toLowerCase()
            ) {
              const match = // check expected path
                _checkForExisting(newPath, reference) ||
                // if requested, check the alternate path, too
                (checkBoth &&
                  renameReference &&
                  _checkForExisting(newPath, !reference));
              if (match) {
                if (match.hidden) {
                  return `The path "${newPath}" is restricted and cannot be used.`;
                }
                const type = match.reference ? 'Reference' : 'Project';
                const dir =
                  match.directory ||
                  match.path.toLowerCase() !== newPath.toLowerCase();
                return `There is an existing ${type} ${dir ? 'Directory' : 'File'} at "${dir ? newPath : match.path}".`;
              } else if (svc.solution) {
                const parent = svc.findParentFile(newPath);
                if (parent && parent.$access !== 'readwrite') {
                  return `The path "${newPath}" is restricted and cannot be used.`;
                }
              }
            }
          };
        };

        const _warningCheck = () => {
          const ignoreCheck = svc.challenge.createIgnoreCheckFunction();
          return (path, { directory }) => {
            if (ignoreCheck(path)) {
              return svc.solution
                ? `Note: This path is not included when assessing your solution, but you can use it in your own tests.\
<br>(Your edits will still be saved, and can be viewed by anyone reviewing your code.)`
                : `Note: If a candidate changes ${directory ? 'files here, they' : 'this file, it'} will be ignored when assessing their solution.\
<br>(You will still be able to review their edits after submission.)`;
            }
          };
        };

        const _renameFile = (svc.renameFile = ({
          file,
          path = file.path,
          reference = false,
          newPath,
          renameReference,
          directory = file.directory,
        }) => {
          if ((file && file.locked) || svc.expired) return;
          if (newPath !== path) {
            if (directory) {
              const pathTest = existingFileRegex(path);
              const _renameChild = (f) => {
                const oldPath = f.path;
                if (!f.locked && pathTest.test(oldPath)) {
                  f.path = newPath + oldPath.slice(path.length);
                  workspace.renameFile(f, oldPath);
                }
              };
              _files(reference).forEach(_renameChild);
              if (renameReference) {
                _files(!reference).forEach(_renameChild);
              }
            } else {
              const relFile = svc.findRelatedFile(file);
              if (renameReference && relFile) {
                relFile.path = newPath;
                workspace.renameFile(relFile, path);
              }
              file.path = newPath;
              workspace.renameFile(file, path);
            }
            // notify others
            if (svc.interact && svc.interact.canEdit) {
              svc.interact.renameFile({ oldPath: path, newPath });
            }

            return true;
          }
        });

        /**
         * Creates a file. If a file already exists with the same path, we update it's contents instead.
         *
         * @param {*} props - new file properties
         * @param {string} props.path - new file path (required)
         * @param {string} props.contents - new file contents (required)
         * @param {boolean?} reference - referenceFile or regular file
         * @param {boolean?} click - Click the file to highlight and open it
         * @param {boolean?} alertOnError - if false, silently ignore errors
         *
         * @returns {CodeFile|boolean} New file (or existing file if one already exists), or false if unable to create or update the file
         * @private
         */
        const _createFile = (svc.createFile = (
          props,
          reference = false,
          click = true,
          alertOnError = true,
        ) => {
          // See if we have an existing file, if so, update it
          const existingFile = svc.findFile(props.path, reference);
          if (existingFile) {
            if (!!existingFile.directory !== !!props.directory) {
              if (alertOnError) {
                appAlert.warning(
                  `Trying to create a new ${props.directory ? 'directory' : 'file'} over an existing same path: ${props.path}`,
                );
              }
              return false;
            }
            if (existingFile.disabled || existingFile.readOnly) {
              if (alertOnError) {
                appAlert.warning(
                  `Trying to modify an existing locked file or directory: ${props.path}`,
                );
              }
              return false;
            }
            if (!existingFile.directory) {
              existingFile.contents = props.contents;
            }
            if (existingFile.path !== props.path) {
              // case changed on path
              _renameFile({
                file: existingFile,
                newPath: props.path,
                reference,
              });
            } else {
              workspace.updateFile(existingFile);
            }
            return existingFile;
          }

          // don't allow creating too many files (slows performance, creates overly complex challenges)
          if (_alertOnTooManyFiles(props, reference)) {
            return false;
          }

          const Model = svc.solution
            ? AdvancedCodeSolutionFile
            : reference
              ? AdvancedCodeChallengeReferenceFile
              : AdvancedCodeChallengeFile;
          const newFileProps = angular.copy(props);
          delete newFileProps.id; // in case we're cloning a file
          if (svc.solution && svc.challenge) {
            const challengeFile = svc.challenge.files.find(
              (cf) => cf.path === newFileProps.path,
            );
            newFileProps.challengeFileId = challengeFile && challengeFile.id;
          }
          const newFile = Model.createInstance(newFileProps);
          _files(reference).push(newFile);
          workspace.addFile(newFile);
          if (click) {
            $timeout(() => {
              svc.clickFile(newFile);
            }, 50);
          }
          // notify others
          if (svc.interact && svc.interact.canEdit) {
            svc.interact.createFile({
              id: newFile.path,
              properties: newFileProps,
            });
          }
          return newFile;
        });

        /**
         * Returns a file matched by path or classic code field
         * @param {string?} pathOrField - Path or field to match on
         * @param {boolean?} reference - If true, search reference files
         * @returns {CodeFile|undefined} Matching file if found
         */
        svc.findFile = (pathOrField, reference = false) => {
          if (pathOrField) {
            const pathTest = existingFileRegex(pathOrField, true);
            const fieldName = pathOrField.replace(/\.[a-z]+$/i, '');
            return _files(reference).find(
              (f) =>
                pathTest.test(f.path) || (f.field && f.field === fieldName),
            );
          }
        };
        svc.findParentFile = (path, reference = false) => {
          let file;
          path = cleanPath(path);
          while (!file && path.length) {
            file = svc.findFile(path, reference);
            path = path.replace(/(^|\/)[^\/]+$/, '');
          }
          return file;
        };

        svc.findRelatedFile = (file) => {
          return (
            !!svc.referenceFiles && svc.findFile(file.path, !file.reference)
          );
        };

        const addFile = (svc.addFile = ({
          basePath = '',
          contents = '',
          prompt,
          placeholder,
          reference,
          directory,
        } = {}) => {
          if (svc.expired || _alertOnTooManyFiles({ directory }, reference))
            return;

          multiFilePathDialog
            .open({
              path: basePath,
              prompt,
              placeholder,
              directory,
              showDirectoryToggle: true,
              isReference: reference,
              invalid: _invalidCheck(null, reference),
              warning: _warningCheck(),
            })
            .then(({ path, directory }) => {
              _createFile({ path, contents, directory }, reference);
              svc.changeReason = 'addFile';
              updateAllAfterFileChanges();
            });
        });

        svc.addFileTo = (opts) => {
          opts.basePath = opts.basePath || '';
          if (!/\/$/.test(opts.basePath)) opts.basePath += '/';
          addFile(opts);
        };

        svc.canAddFileTo = (file, path, directory) => {
          if (!directory) return false;
          if (file) return file.$access === 'readwrite';
          let parentFile = svc.findParentFile(path);
          return !parentFile || parentFile.$access === 'readwrite';
        };

        svc.rename = (
          file,
          path = file.path,
          reference = file && file.reference,
          newPath = path,
        ) => {
          if ((file && file.locked) || svc.expired) return;
          const directory = !file || file.directory;
          const hasReference =
            svc.referenceFiles && (directory || (file && file.hasReference));
          multiFilePathDialog
            .open({
              path: newPath,
              title: `Rename or Move ${directory ? 'Directory' : 'File'}`,
              prompt: 'New path & name:',
              isReference: reference,
              hasReference,
              directory,
              invalid: _invalidCheck(path, reference, hasReference),
              warning: _warningCheck(),
            })
            .then(({ path: newPath, renameReference }) => {
              const renamed = _renameFile({
                file,
                path,
                reference,
                newPath,
                renameReference,
                directory,
              });
              if (renamed) {
                svc.changeReason = 'renameFile';
                updateAllAfterFileChanges();
              }
            });
        };

        svc.close = (file) => {
          ArrayUtils.remove(svc.openFiles, file);
        };

        svc.deletedFiles = [];
        const _addToDeleted = (file) => {
          // We only ever store copies of deleted files, to prevent reference issues
          const toDelete = angular.copy(file);
          delete toDelete.id;
          if (toDelete.reference) {
            // mark reference files so we don't have to worry about collisions
            toDelete.path = REFERENCE_PREFIX + toDelete.path;
          }
          toDelete.$deleted = true;

          const existingIndex = svc.deletedFiles.findIndex(
            (df) =>
              df.path === toDelete.path && df.directory === toDelete.directory,
          );
          if (existingIndex > -1) {
            // simply overwrite existing deleted file
            svc.deletedFiles[existingIndex] = toDelete;
          } else {
            if (!file.directory) {
              // purge any deleted files that would be descendant of this file
              const purgePath = existingFileRegex(toDelete.path);
              removeMatching(svc.deletedFiles, (df) => purgePath.test(df.path));
            }

            // purge any deleted files that match parent directories
            const parents = toDelete.path.split('/');
            parents.pop(); // skip the exact path
            while (parents.length) {
              const purgePath = parents.join('/');
              const purgeIndex = svc.deletedFiles.findIndex(
                (df) => df.path === purgePath && !df.directory,
              );
              if (purgeIndex > -1) {
                svc.deletedFiles.splice(purgeIndex, 1);
              }
              parents.pop();
            }

            svc.deletedFiles.push(toDelete);
          }
        };
        const _deleteFile = (file) => {
          if (file.locked) return;
          svc.close(file);
          // Always remove from active files array
          ArrayUtils.remove(_files(file.reference), file);
          // Set softDeletedAt for files
          file.softDeletedAt = new Date();
          // Add to deleted files list
          _addToDeleted(file);
          workspace.deleteFile(file);
        };
        svc.delete = (
          file,
          path = file.path,
          reference = file && file.reference,
          delReference,
        ) => {
          if ((file && file.locked) || svc.expired) return;

          const directory = !file || file.directory;
          if (svc.referenceFiles && delReference === undefined) {
            let msg;
            if (directory) {
              const pathTest = existingFileRegex(path);
              if (
                _files(!reference).some(
                  (f) => pathTest.test(f.path) && !f.locked,
                )
              ) {
                msg = `Also delete any ${reference ? 'Project' : 'Reference'} Files under "${path}"?`;
              }
            } else if (file.hasReference) {
              msg = `Also delete the associated ${reference ? 'Project' : 'Reference'} File?`;
            }
            // only ask if there are any matching reference files
            if (msg) {
              dialogService
                .confirm(msg, 'Delete Related Files', ['Cancel', 'No', 'Yes'])
                .then((btn) => {
                  svc.delete(file, path, reference, btn.id === 'yes');
                });
              return;
            }
          }
          if (!directory) {
            const relFile = svc.findRelatedFile(file);
            if (delReference && relFile) {
              _deleteFile(relFile);
            }
            _deleteFile(file);
          } else {
            const pathTest = existingFileRegex(path);
            if (!reference || delReference) {
              svc.files
                .filter((f) => pathTest.test(f.path) && !f.locked)
                .forEach(_deleteFile);
            }
            if (reference || delReference) {
              svc.referenceFiles
                .filter((f) => pathTest.test(f.path) && !f.locked)
                .forEach(_deleteFile);
            }
          }
          svc.changeReason = 'deleteFile';
          updateAllAfterFileChanges();
          // notify others
          if (svc.interact && svc.interact.canEdit) {
            svc.interact.deleteFile({ id: file.path });
          }
        };

        const _getDeletedFiles = (file, path) => {
          if (!file || file.directory) {
            const pathTest = existingFileRegex(path);
            return svc.deletedFiles.filter((f) => pathTest.test(f.path));
          } else {
            return [file];
          }
        };

        svc.undelete = (file, path = file && file.path) => {
          if (!path || svc.expired) return;

          const reference = file
            ? file.reference
            : REFERENCE_PREFIX_REGEX.test(path);

          const filesToRestore = _getDeletedFiles(file, path);

          multiFileOverwriteDialog
            .open({
              paths: filesToRestore.map((f) =>
                f.path.replace(REFERENCE_PREFIX_REGEX, ''),
              ),
              files: _files(reference),
            })
            .then(({ paths }) => {
              filesToRestore
                .filter((f) =>
                  paths.includes(f.path.replace(REFERENCE_PREFIX_REGEX, '')),
                )
                .forEach((file) => {
                  const files = _files(reference);
                  ArrayUtils.remove(svc.deletedFiles, file);
                  if (file.reference) {
                    // clean the reference file trick
                    file.path = file.path.replace(REFERENCE_PREFIX_REGEX, '');
                  }

                  // remove the overwritten file if there is one
                  const overwrittenFile = svc.findFile(file.path, reference);
                  if (overwrittenFile) {
                    _deleteFile(overwrittenFile);
                  }
                  file.$deleted = false;
                  files.push(file);
                  workspace.addFile(file);
                  if (filesToRestore.length < 3) {
                    svc.clickFile(file);
                  }
                });
              svc.changeReason = 'restoreFile';
              updateAllAfterFileChanges();
            });
        };

        svc.purge = (file, path = file && file.path) => {
          if (!path || svc.expired) return;

          _getDeletedFiles(file, path).forEach((fileToPurge) => {
            svc.close(fileToPurge);
            ArrayUtils.remove(svc.deletedFiles, fileToPurge);
          });
        };

        // only used by assess, resets a solution file to a challenge file
        const _resetFile = (file) => {
          if (file.disabled || file.readOnly) return;

          const originalFile = svc.challenge.files.find(
            (f) => f.path === file.path,
          );

          if (originalFile) {
            if (originalFile.contents === file.contents) return;
            if (!file.locked) {
              // back up a copy of the file to deleted if we can
              _addToDeleted(file);
            }
            file.contents = originalFile.contents;
            workspace.updateFile(file);
          } else if (!file.locked) {
            // just delete if we can
            _deleteFile(file);
          }
        };

        svc.reset = (file) => {
          if (svc.expired) return;
          _resetFile(file);
          svc.changeReason = 'resetFile';
          updateAllAfterFileChanges();
        };
        // only used by assess, resets all solutions files to challenge files
        svc.resetAll = () => {
          if (svc.expired) return;
          svc.files.slice().forEach(_resetFile);
          svc.challenge.files.forEach((cf) => {
            if (!svc.files.some((sf) => sf.path === cf.path)) {
              // restore original files that were deleted
              const props = {};
              [
                'path',
                'contents',
                'access',
                'deletable',
                'directory',
                'challengeFileId',
                'includeOnTest',
                'includeOnSubmit',
                'initialLayout',
                'testGroups',
              ].forEach((k) => {
                props[k] = cf[k];
              });
              _createFile(props, false, false);
            }
          });
          svc.changeReason = 'resetAll';
          updateAllAfterFileChanges();
        };

        svc.export = (file) => {
          virtualFileDownloadService.download(file.path, file.contents);
        };

        function updateAllAfterFileChanges() {
          if (svc.refreshOnFilesChanged) {
            // refresh the controllers as well
            svc.refreshOnFilesChanged();
          } else {
            _updateAfterFileChanges();
          }
        }

        function _updateAfterFileChanges() {
          if (svc.referenceFiles) {
            svc.files.forEach((f) => {
              f.hasReference = false;
            });
            svc.referenceFiles.forEach((refFile) => {
              const setupFile = svc.findFile(refFile.path);
              if (setupFile) {
                setupFile.hasReference = refFile.hasReference = true;
              } else {
                refFile.hasReference = false;
              }
            });
          }

          if (svc.solution && svc.challenge) {
            const readOnly =
              !!svc.readOnly || (svc.interact && svc.interact.cannotEdit);
            const restricted = !!svc.restricted;

            const isIgnored = svc.challenge.createIgnoreCheckFunction();
            // flag files that can be reset or are ignored
            svc.files.forEach((f) => {
              const challengeFile = svc.challenge.files.find(
                (cf) => cf.path === f.path,
              );
              f.canReset = challengeFile && !challengeFile.directory;
              f.unchanged =
                challengeFile && challengeFile.contents === f.contents;
              f.hasChallengeFile = !!challengeFile;
              f.restricted = restricted;
              f.ignored =
                f.$access === 'readwrite' &&
                (!f.includeOnSubmit || isIgnored(f.path));
              f.lockedLayout = svc.challenge.lockedLayout();
              f.$readOnly = readOnly;
              f.$interact = !!svc.interact;
            });
          }

          // TODO: this might be too inefficient, so we might need to determine which files are changed and only
          //   save those files. Or, we might want to move this logic to CodeMirrorWorkspace, and have
          //   that component track what contents might have changed since then.
          workspace.saveAllFiles();

          if (opts.afterFileChanges) {
            opts.afterFileChanges(svc.changeReason);
            delete svc.changeReason;
          }
        }

        svc.updateAfterFileChanges = _updateAfterFileChanges;

        svc.openRelatedFile = (file) => {
          const relFile = svc.findRelatedFile(file);
          if (relFile) {
            svc.clickFile(relFile);
          } else {
            _createFile(file, !file.reference);
          }
        };

        const DUPLICATE_PROPS = ['path', 'contents', 'directory'];
        if (!svc.solution) {
          DUPLICATE_PROPS.push(
            'access',
            'includeOnTest',
            'includeOnSubmit',
            'deletable',
            'testGroups',
            'initialLayout',
          );
        }

        svc.duplicate = (
          existingFile,
          existingPath = existingFile.path,
          reference = existingFile && existingFile.reference,
        ) => {
          if ((existingFile && existingFile.disabled) || svc.expired) return;

          const directory = !existingFile || existingFile.directory;
          const targetFiles = _files(reference);
          let dialogResult;
          let newFiles = [existingFile];
          const pathMatcher = existingFileRegex(existingPath);
          if (directory) {
            newFiles = targetFiles.filter(
              (f) => pathMatcher.test(f.path) && (!svc.solution || !f.disabled),
            );
            dialogResult = multiFilePathDialog.open({
              title: `Duplicate Directory "${existingPath}"`,
              prompt: 'Where should the new directory be added?',
              path: existingPath,
              directory: true,
              allowRoot: false,
              invalid: _invalidCheck(null, reference),
              warning: _warningCheck(),
            });
          } else {
            dialogResult = multiFilePathDialog.open({
              title: `Duplicate "${existingPath}"`,
              prompt: 'Path for new file',
              path: existingPath,
              invalid: _invalidCheck(null, reference),
              warning: _warningCheck(),
            });
          }
          newFiles = newFiles.map((f) => {
            const newProps = {};
            DUPLICATE_PROPS.forEach((k) => {
              newProps[k] = angular.copy(f[k]);
            });
            return newProps;
          });
          dialogResult.then(({ path: newPath }) => {
            const tail = directory ? '/' : '';
            let openedCount = 0;
            const filesToOpen = [];
            newFiles.forEach((newFileProps) => {
              if (newFileProps.path === existingPath) {
                newFileProps.path = newPath;
              } else {
                newFileProps.path = newFileProps.path.replace(
                  pathMatcher,
                  newPath + tail,
                );
              }
              // only auto-open the first 4 files
              openedCount++;
              const newFile = _createFile(newFileProps, reference, false);
              if (newFile && openedCount < 5) {
                filesToOpen.push(newFile);
              }
            });

            filesToOpen.forEach(svc.clickFile);

            svc.changeReason = 'duplicateFile';
            updateAllAfterFileChanges();
          });
        };

        multiFileChallengeServiceDND.setupDragAndDrop(svc, _createFile);

        _updateAfterFileChanges();
      }

      return {
        setupFileManagement,
      };
    },
  );
